/**
 * Ops Portal - Cold Chain Monitoring Dashboard
 * Temperature and Humidity tracking for customer dispatchers
 */
import { useState, useEffect, useCallback } from 'react';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Thermometer, Droplets, Map, AlertTriangle, CheckCircle,
  RefreshCw, Loader2, Wifi, WifiOff, Battery, TrendingUp,
  Download, MapPin, Send, Package
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, ComposedChart, Legend, ReferenceLine
} from 'recharts';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';

export default function OpsColdChainPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [timeRange, setTimeRange] = useState(24);
  
  const [dashboard, setDashboard] = useState({});
  const [liveData, setLiveData] = useState({ shipments: [] });
  const [trends, setTrends] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, liveRes, trendsRes] = await Promise.all([
        opsApi.getColdChainDashboard(),
        opsApi.getColdChainLive(),
        opsApi.getColdChainTrends({ hours: timeRange })
      ]);
      
      setDashboard(dashboardRes.data || {});
      setLiveData(liveRes.data || { shipments: [] });
      setTrends(trendsRes.data?.data || []);
    } catch (error) {
      toast.error('Failed to load cold chain data');
    } finally {
      setLoading(false);
    }
     
  }, [timeRange]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
     
  }, [fetchData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-emerald-500';
      case 'warning': return 'text-amber-500';
      case 'breach': return 'text-red-500';
      default: return 'text-slate-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'normal': return 'bg-emerald-500/10';
      case 'warning': return 'bg-amber-500/10';
      case 'breach': return 'bg-red-500/10';
      default: return 'bg-slate-500/10';
    }
  };

  if (loading && !dashboard.active_shipments) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-400">Loading cold chain data...</p>
        </div>
      </div>
    );
  }

  const temp = dashboard.temperature || {};
  const humidity = dashboard.humidity || {};

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Cold Chain Monitoring</h1>
          <p className="text-slate-400">Real-time temperature and humidity tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={fetchData}
            disabled={loading}
            className="border-slate-600 text-slate-300"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Active</span>
          </div>
          <p className="text-2xl font-bold text-white">{dashboard.active_shipments || 0}</p>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Thermometer className="w-5 h-5 text-emerald-400" />
            <span className="text-slate-400 text-sm">Temp OK</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{temp.normal || 0}</p>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Thermometer className="w-5 h-5 text-amber-400" />
            <span className="text-slate-400 text-sm">Temp Warning</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{temp.warning || 0}</p>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-slate-400 text-sm">Temp Breach</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{temp.breach || 0}</p>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Droplets className="w-5 h-5 text-cyan-400" />
            <span className="text-slate-400 text-sm">Humidity OK</span>
          </div>
          <p className="text-2xl font-bold text-cyan-400">{humidity.normal || 0}</p>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wifi className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Online</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{dashboard.devices_online || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="live" className="data-[state=active]:bg-blue-600">
            <Map className="w-4 h-4 mr-2" />
            Live View
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-blue-600">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trends
          </TabsTrigger>
        </TabsList>

        {/* Live View Tab */}
        <TabsContent value="live" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Live Shipment Map */}
            <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-lg p-4 h-[450px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">Live Shipment Map</h3>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2" />
                  Live
                </Badge>
              </div>
              <div className="h-[380px] rounded-lg overflow-hidden">
                <MapContainer
                  center={[28.6139, 77.2090]}
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                  className="z-0"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {liveData.shipments?.map((shipment, idx) => {
                    const lat = shipment.device_lat || (28.5 + Math.random() * 0.4);
                    const lng = shipment.device_lng || (77.0 + Math.random() * 0.4);
                    const temp = shipment.temperature?.value;
                    const isBreached = shipment.temperature?.status === 'critical' || shipment.temperature?.status === 'warning';
                    return (
                      <CircleMarker
                        key={shipment.trip_id || `ship-${idx}`}
                        center={[lat, lng]}
                        radius={8}
                        pathOptions={{
                          fillColor: isBreached ? '#ef4444' : '#10b981',
                          color: isBreached ? '#ef4444' : '#10b981',
                          weight: 2,
                          fillOpacity: 0.8
                        }}
                      >
                        <Popup>
                          <div className="text-sm min-w-[160px]">
                            <p className="font-bold">{shipment.trip_number}</p>
                            <p className="text-slate-600 text-xs">{shipment.destination}</p>
                            {temp !== null && temp !== undefined && (
                              <p className={isBreached ? 'text-red-600 font-medium' : 'text-green-600'}>
                                Temp: {temp}°C
                              </p>
                            )}
                            <p className="text-xs mt-1">
                              {shipment.is_online ? '🟢 Online' : '🔴 Offline'}
                            </p>
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              </div>
            </div>

            {/* Shipment List */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 h-[450px] overflow-y-auto">
              <h3 className="font-semibold text-white mb-4">Active Shipments</h3>
              <div className="space-y-3">
                {liveData.shipments?.length === 0 ? (
                  <p className="text-center py-8 text-slate-400">No active shipments</p>
                ) : (
                  liveData.shipments?.map((shipment, idx) => (
                    <div 
                      key={shipment.trip_id || idx}
                      className="p-3 bg-slate-700/50 border border-slate-600 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white text-sm">{shipment.trip_number}</span>
                        <div className="flex items-center gap-2">
                          {shipment.is_online ? (
                            <Wifi className="w-4 h-4 text-green-400" />
                          ) : (
                            <WifiOff className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mb-2 truncate">{shipment.destination}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className={cn("px-2 py-1.5 rounded text-center", getStatusBg(shipment.temperature?.status))}>
                          <Thermometer className={cn("w-3 h-3 mx-auto mb-1", getStatusColor(shipment.temperature?.status))} />
                          <span className={cn("text-xs font-medium", getStatusColor(shipment.temperature?.status))}>
                            {shipment.temperature?.value !== null ? `${shipment.temperature.value}°C` : 'N/A'}
                          </span>
                        </div>
                        <div className={cn("px-2 py-1.5 rounded text-center", getStatusBg(shipment.humidity?.status))}>
                          <Droplets className={cn("w-3 h-3 mx-auto mb-1", getStatusColor(shipment.humidity?.status))} />
                          <span className={cn("text-xs font-medium", getStatusColor(shipment.humidity?.status))}>
                            {shipment.humidity?.value !== null ? `${shipment.humidity.value}%` : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Battery className={cn("w-3 h-3", shipment.battery_level > 20 ? 'text-green-400' : 'text-red-400')} />
                          <span className="text-xs text-slate-400">{shipment.battery_level || 0}%</span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-400">
                          <Send className="w-3 h-3 mr-1" />
                          Send QR
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-6">
          <div className="flex items-center justify-end mb-4">
            <Select value={timeRange.toString()} onValueChange={(v) => setTimeRange(parseInt(v))}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Last 6 hours</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="72">Last 3 days</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Temperature Chart */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <h3 className="font-semibold text-white mb-4">Temperature Trends</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      stroke="#94a3b8"
                    />
                    <YAxis domain={[0, 15]} stroke="#94a3b8" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                      labelFormatter={(v) => new Date(v).toLocaleString()}
                    />
                    <Legend />
                    <ReferenceLine y={2} stroke="#f59e0b" strokeDasharray="5 5" />
                    <ReferenceLine y={8} stroke="#f59e0b" strokeDasharray="5 5" />
                    <Area type="monotone" dataKey="max_temp" fill="#ef444420" stroke="#ef4444" name="Max" />
                    <Line type="monotone" dataKey="avg_temp" stroke="#3b82f6" strokeWidth={2} dot={false} name="Avg" />
                    <Area type="monotone" dataKey="min_temp" fill="#22c55e20" stroke="#22c55e" name="Min" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Humidity Chart */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <h3 className="font-semibold text-white mb-4">Humidity Trends</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      stroke="#94a3b8"
                    />
                    <YAxis domain={[0, 100]} stroke="#94a3b8" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                      labelFormatter={(v) => new Date(v).toLocaleString()}
                    />
                    <Legend />
                    <ReferenceLine y={35} stroke="#f59e0b" strokeDasharray="5 5" />
                    <ReferenceLine y={65} stroke="#f59e0b" strokeDasharray="5 5" />
                    <Area type="monotone" dataKey="max_humidity" fill="#8b5cf620" stroke="#8b5cf6" name="Max" />
                    <Line type="monotone" dataKey="avg_humidity" stroke="#06b6d4" strokeWidth={2} dot={false} name="Avg" />
                    <Area type="monotone" dataKey="min_humidity" fill="#14b8a620" stroke="#14b8a6" name="Min" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
